import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, DeleteOutline, Download, Edit, PersonAdd, Search, Visibility } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { useMRTState } from '../../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../../helpers/mrt/getMRTDefaultProps';

const VARIAVEIS_AUTOMATICAS = new Set([
    'associacao.razaoSocial',
    'associacao.nomeFantasia',
    'associacao.cnpj',
    'associacao.endereco',
    'documento.titulo',
    'documento.dataEmissao',
    'documento.localEmissao',
    'gestaoAtual.descricao',
    'gestaoAtual.dataInicio',
    'gestaoAtual.dataFim',
    'gestaoAtual.presidente.nome',
    'gestaoAtual.presidente.cpf',
    'gestaoAtual.presidente.endereco',
    'gestaoAnterior.descricao',
    'gestaoAnterior.dataInicio',
    'gestaoAnterior.dataFim',
    'gestaoAnterior.presidente.nome',
    'gestaoAnterior.presidente.cpf',
    'gestaoAnterior.presidente.endereco',
]);
const extrairVariaveis = (texto = '') => [
    ...new Set([...texto.matchAll(/\{\{\s*([A-Za-z][A-Za-z0-9_.-]{0,119})\s*\}\}/g)].map((m) => m[1])),
];

export default function EditorTab({ modulo: m, arquivos, documento, parametro, isMobile }) {
    const mrt = useMRTState({ storageKey: 'ArquivoDocumento.Editor', pageSizeDefault: Number(parametro?.paginacao) || 5 });
    const defaults = getMRTDefaultProps({ mrt, parametro, isMobile });
    const [selecionado, setSelecionado] = useState(null);
    const i = m.item;
    const variaveisTexto = useMemo(() => extrairVariaveis(i?.texto), [i?.texto]);
    const camposVariaveis = useMemo(
        () => [
            ...new Set([
                ...variaveisTexto.filter((chave) => /^campo\.[A-Za-z][A-Za-z0-9_.-]{0,79}$/.test(chave)),
                ...Object.keys(i?.camposVariaveis || {}).filter((chave) => /^campo\.[A-Za-z][A-Za-z0-9_.-]{0,79}$/.test(chave)),
            ]),
        ],
        [variaveisTexto, i?.camposVariaveis]
    );
    const automaticasUsadas = useMemo(() => variaveisTexto.filter((chave) => VARIAVEIS_AUTOMATICAS.has(chave)), [variaveisTexto]);
    const desconhecidas = useMemo(
        () =>
            variaveisTexto.filter(
                (chave) => !VARIAVEIS_AUTOMATICAS.has(chave) && !/^campo\.[A-Za-z][A-Za-z0-9_.-]{0,79}$/.test(chave)
            ),
        [variaveisTexto]
    );
    const options = useMemo(
        () =>
            m.opcoes
                .filter((p) => !i?.assinaturas?.some((s) => String(s.pessoaId) === String(p.pessoaId)))
                .flatMap((p) => [
                    ...(!i?.cargosEletivos ? [{ pessoa: p, cargo: null, label: `${p.nome} — sem cargo` }] : []),
                    ...p.cargos.map((c) => ({ pessoa: p, cargo: c, label: `${p.nome} — ${c.cargo}` })),
                ]),
        [m.opcoes, i?.cargosEletivos, i?.assinaturas]
    );
    useEffect(() => {
        setSelecionado(null);
    }, [options]);
    const columns = useMemo(
        () => [
            { accessorKey: 'titulo', header: 'Documento' },
            {
                accessorKey: 'dataDocumento',
                header: 'Data',
                Cell: ({ cell }) => cell.getValue()?.split('-').reverse().join('/') || '',
            },
            {
                id: 'situacao',
                header: 'Situação',
                accessorFn: (r) => (r.emitidoEm ? 'Emitido' : r.fechado ? 'PDF externo anexado' : 'Rascunho'),
            },
            { id: 'acesso', header: 'Acesso', accessorFn: (r) => (r.apenasSocio ? 'Apenas sócios' : 'Normal') },
        ],
        []
    );
    const bloqueado = !m.podeEditar || Boolean(i?.fechado);
    const texto = (name, label, props = {}) => (
        <TextField
            name={name}
            label={label}
            title={label}
            value={i[name] || ''}
            onChange={(e) => m.alterar(name, e.target.value)}
            disabled={bloqueado}
            size="small"
            {...props}
        />
    );
    const flag = (name, label) => (
        <FormControlLabel
            label={label}
            control={
                <Checkbox checked={Boolean(i[name])} onChange={(e) => m.alterar(name, e.target.checked)} disabled={bloqueado} />
            }
        />
    );
    const mover = (index, passo) => {
        const list = [...i.assinaturas];
        [list[index], list[index + passo]] = [list[index + passo], list[index]];
        m.alterar('assinaturas', list);
    };
    if (!documento.uuid)
        return (
            <Alert severity="info" sx={{ my: 2 }}>
                Salve o cabeçalho para criar ou anexar documentos.
            </Alert>
        );
    return (
        <Stack spacing={2} sx={{ py: 2, minWidth: 0 }}>
            {m.impedido && (
                <Alert severity="info">
                    Conclua ou descarte as alterações do cabeçalho e da aba Arquivos para usar o editor.
                </Alert>
            )}
            {m.erro && (
                <Alert
                    severity="error"
                    action={
                        <Button type="button" disabled={m.ocupado || m.impedido} onClick={m.recarregar}>
                            Recarregar lista
                        </Button>
                    }
                >
                    {m.erro}
                </Alert>
            )}
            <MaterialReactTable
                {...defaults}
                columns={columns}
                data={m.lista}
                getRowId={(r) => r.uuid}
                manualFiltering={false}
                manualSorting={false}
                manualPagination={false}
                rowCount={undefined}
                enableRowSelection={false}
                renderRowActions={
                    isMobile
                        ? undefined
                        : ({ row }) => (
                              <>
                                  <IconButton
                                      title="Abrir texto do documento"
                                      disabled={!m.podeImprimir || m.pendente}
                                      onClick={() => m.abrir(row.original)}
                                  >
                                      <Edit />
                                  </IconButton>
                                  <IconButton
                                      title="Excluir redação e arquivos vinculados"
                                      disabled={!m.podeCancelar || m.pendente}
                                      onClick={() => m.excluir(row.original)}
                                  >
                                      <DeleteOutline />
                                  </IconButton>
                              </>
                          )
                }
                renderRowActionMenuItems={
                    isMobile
                        ? ({ row, closeMenu }) => [
                              <MenuItem
                                  key="abrir"
                                  disabled={!m.podeImprimir || m.pendente}
                                  onClick={() => {
                                      closeMenu();
                                      m.abrir(row.original);
                                  }}
                              >
                                  Abrir texto
                              </MenuItem>,
                              <MenuItem
                                  key="excluir"
                                  disabled={!m.podeCancelar || m.pendente}
                                  onClick={() => {
                                      closeMenu();
                                      m.excluir(row.original);
                                  }}
                              >
                                  Excluir
                              </MenuItem>,
                          ]
                        : undefined
                }
            />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button type="button" variant="outlined" disabled={!m.podeEditar} onClick={m.novo}>
                    Criar documento
                </Button>
                <Button type="button" disabled={!m.podeEditar || !i} onClick={m.copiar}>
                    Criar cópia
                </Button>
                <Button type="button" disabled={m.ocupado || m.impedido} onClick={m.limpar}>
                    {m.pendente ? 'Descartar edição' : 'Limpar seleção'}
                </Button>
            </Stack>
            {i && (
                <>
                    {i.fechado && <Alert severity="info">Texto e assinaturas preservados. Para alterar, crie uma cópia.</Alert>}
                    {texto('titulo', 'Título do documento', { inputProps: { maxLength: 255 } })}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        {texto('dataDocumento', 'Data do documento', { type: 'date', InputLabelProps: { shrink: true } })}
                        {texto('localEmissao', 'Local de emissão (cidade/UF)', {
                            fullWidth: true,
                            inputProps: { maxLength: 255 },
                        })}
                    </Stack>
                    {texto('texto', 'Texto do documento', {
                        multiline: true,
                        minRows: 12,
                        inputProps: { maxLength: 100000 },
                        helperText:
                            'Cada quebra de linha será mantida. Use {{campo.nome}} para valores informados nesta emissão e variáveis automáticas para dados do sistema.',
                    })}
                    <Alert severity="info">
                        Exemplos: {'{{associacao.razaoSocial}}'}, {'{{gestaoAtual.dataInicio}}'},{' '}
                        {'{{gestaoAnterior.presidente.nome}}'} e {'{{campo.prazoResposta}}'}. Os dados da gestão são obtidos da
                        eleição vigente na data do documento.
                    </Alert>
                    {automaticasUsadas.length > 0 && (
                        <Typography variant="body2">
                            Variáveis automáticas detectadas: {automaticasUsadas.map((chave) => `{{${chave}}}`).join(', ')}
                        </Typography>
                    )}
                    {desconhecidas.length > 0 && (
                        <Alert severity="warning">
                            Variáveis não reconhecidas: {desconhecidas.map((chave) => `{{${chave}}}`).join(', ')}. Corrija antes
                            de salvar ou emitir.
                        </Alert>
                    )}
                    {camposVariaveis.length > 0 && (
                        <Stack spacing={1}>
                            <Typography variant="h6">Campos desta emissão</Typography>
                            <Typography variant="body2">
                                Estes valores ficam preservados na redação e não alteram os cadastros do sistema.
                            </Typography>
                            {camposVariaveis.map((chave) => (
                                <TextField
                                    key={chave}
                                    size="small"
                                    label={chave.slice('campo.'.length).replace(/[._-]+/g, ' ')}
                                    title={`Valor de {{${chave}}}`}
                                    value={i.camposVariaveis?.[chave] || ''}
                                    onChange={(e) => m.alterarCampoVariavel(chave, e.target.value)}
                                    disabled={bloqueado}
                                    multiline
                                    maxRows={4}
                                    inputProps={{ maxLength: 5000 }}
                                    helperText={`Substitui {{${chave}}} no PDF`}
                                />
                            ))}
                        </Stack>
                    )}
                    {i.fechado && i.variaveisResolvidas && (
                        <Typography variant="caption">
                            Os valores usados na emissão foram preservados com o texto final do PDF.
                        </Typography>
                    )}
                    {flag('apenasSocio', 'Apenas sócios podem visualizar o conteúdo e os PDFs')}
                    <Typography variant="h6">Responsável e assinaturas</Typography>
                    <Typography variant="body2">
                        O criador é incluído inicialmente. Você pode escolher outra pessoa para assinar, com ou sem cargo, e
                        ajustar a ordem.
                    </Typography>
                    {flag('cargosEletivos', 'Cargos eletivos')}
                    {i.cargosEletivos && (
                        <Stack direction="row" useFlexGap flexWrap="wrap">
                            {flag('diretoria', 'Diretoria')}
                            {flag('conselho', 'Conselho fiscal')}
                        </Stack>
                    )}
                    {!i.fechado && (
                        <>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Autocomplete
                                    options={options}
                                    value={selecionado}
                                    onChange={(_, value) => setSelecionado(value)}
                                    inputValue={selecionado?.label || m.pesquisa}
                                    onInputChange={(_, value, reason) => {
                                        if (reason === 'input' || reason === 'clear') {
                                            setSelecionado(null);
                                            m.setPesquisa(value);
                                        }
                                    }}
                                    filterOptions={(values) => values}
                                    loading={m.buscandoPessoas}
                                    loadingText="Buscando signatários..."
                                    isOptionEqualToValue={(a, b) =>
                                        a.pessoa.pessoaId === b.pessoa.pessoaId && a.cargo?.cargoId === b.cargo?.cargoId
                                    }
                                    disabled={bloqueado}
                                    fullWidth
                                    noOptionsText={
                                        i.cargosEletivos && !i.diretoria && !i.conselho
                                            ? 'Marque Diretoria e/ou Conselho fiscal para pesquisar.'
                                            : 'Nenhum novo signatário encontrado. Altere o nome ou confira os vínculos vigentes.'
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            size="small"
                                            label="Pesquisar pessoa para assinatura"
                                            placeholder="Digite o nome do próximo signatário"
                                            helperText="A busca consulta novos nomes enquanto você digita. Quem já foi adicionado não aparece novamente."
                                        />
                                    )}
                                />
                                <Button
                                    type="button"
                                    variant="contained"
                                    color="primary"
                                    disableElevation
                                    startIcon={m.buscandoPessoas ? <CircularProgress size={18} color="inherit" /> : <Search />}
                                    sx={{
                                        minWidth: { sm: 150 },
                                        height: 40,
                                        flexShrink: 0,
                                        borderRadius: 2,
                                        textTransform: 'none',
                                    }}
                                    title="Atualizar a busca de signatários pelo nome informado"
                                    disabled={bloqueado || m.buscandoPessoas || (i.cargosEletivos && !i.diretoria && !i.conselho)}
                                    onClick={() => m.buscarPessoas()}
                                >
                                    {m.buscandoPessoas ? 'Buscando...' : 'Buscar pessoas'}
                                </Button>
                            </Stack>
                            <Button
                                type="button"
                                variant="outlined"
                                startIcon={<PersonAdd />}
                                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' }, textTransform: 'none' }}
                                disabled={bloqueado || !selecionado}
                                onClick={() => {
                                    m.adicionar(selecionado.pessoa, selecionado.cargo);
                                    setSelecionado(null);
                                }}
                            >
                                Adicionar à lista de assinaturas
                            </Button>
                            {m.erroBusca && <Alert severity="error">{m.erroBusca}</Alert>}
                            {i.cargosEletivos && (
                                <Typography variant="caption">
                                    Os filtros mostram ocupantes com vínculo vigente no RH. Revise e adicione quem deverá assinar.
                                </Typography>
                            )}
                        </>
                    )}
                    {i.assinaturas.map((s, index) => (
                        <Box key={s.pessoaId} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: 1, minWidth: 160 }}>
                                <Typography>
                                    {index + 1}. {s.nome}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {[s.cargo, s.cpf ? `CPF: ${s.cpf}` : 'CPF não informado'].filter(Boolean).join(' — ')}
                                </Typography>
                            </Box>
                            <IconButton
                                title="Mover assinatura para cima"
                                disabled={bloqueado || index === 0}
                                onClick={() => mover(index, -1)}
                            >
                                <ArrowUpward />
                            </IconButton>
                            <IconButton
                                title="Mover assinatura para baixo"
                                disabled={bloqueado || index === i.assinaturas.length - 1}
                                onClick={() => mover(index, 1)}
                            >
                                <ArrowDownward />
                            </IconButton>
                            <IconButton
                                title="Remover signatário"
                                disabled={bloqueado}
                                onClick={() =>
                                    m.alterar(
                                        'assinaturas',
                                        i.assinaturas.filter((_, idx) => idx !== index)
                                    )
                                }
                            >
                                <DeleteOutline />
                            </IconButton>
                        </Box>
                    ))}
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Button type="button" variant="contained" disabled={bloqueado || !m.sujo} onClick={m.salvar}>
                            Salvar documento
                        </Button>
                        <Button
                            type="button"
                            startIcon={<Visibility />}
                            disabled={!m.podeImprimir || m.pendente || !i.uuid || i.fechado}
                            onClick={m.visualizar}
                        >
                            Visualizar prévia
                        </Button>
                        <Button
                            type="button"
                            disabled={!m.podeEditar || !m.podeImprimir || m.pendente || !i.uuid || i.fechado}
                            onClick={m.emitir}
                        >
                            Emitir PDF
                        </Button>
                        <Button
                            type="button"
                            component="label"
                            disabled={
                                !m.podeEditar || m.sujo || !i.uuid || i.arquivos?.some((a) => a.origemRedacao === 'assinado')
                            }
                        >
                            Selecionar PDF assinado / externo
                            <input hidden type="file" accept="application/pdf,.pdf" onChange={m.escolherArquivo} />
                        </Button>
                        <Button type="button" disabled={!m.podeEditar || m.sujo || !m.arquivo} onClick={m.anexar}>
                            Enviar PDF
                        </Button>
                    </Stack>
                    {m.arquivo && <Typography variant="body2">Selecionado: {m.arquivo.name}</Typography>}
                    {i.arquivos?.map((a) => (
                        <Stack key={a.uuid} direction="row" alignItems="center" spacing={1}>
                            <Typography sx={{ flex: 1 }}>
                                {a.origemRedacao === 'emitido' ? 'PDF emitido' : 'PDF assinado / externo'}
                            </Typography>
                            <IconButton
                                title="Visualizar PDF"
                                disabled={!m.podeImprimir || m.pendente}
                                onClick={() => arquivos.abrir(a, false, { novaAba: true })}
                            >
                                <Visibility />
                            </IconButton>
                            <IconButton
                                title="Baixar PDF"
                                disabled={!m.podeImprimir || m.pendente}
                                onClick={() => arquivos.abrir(a, true)}
                            >
                                <Download />
                            </IconButton>
                        </Stack>
                    ))}
                    {arquivos.preview?.mimeType === 'application/pdf' && (
                        <Alert severity="info">
                            Se o navegador não abriu o PDF,{' '}
                            <a href={arquivos.preview.url} target="_blank" rel="noopener noreferrer">
                                abra o documento
                            </a>
                            {' ou '}
                            <a href={arquivos.preview.url} download={arquivos.preview.nome}>
                                baixe o arquivo
                            </a>
                            .
                        </Alert>
                    )}
                </>
            )}
        </Stack>
    );
}
